import { useMemo } from 'react'
import { useTenant } from '@/contexts'
import { useUserPreferences } from '@/contexts'

interface Location {
  id: string
  name: string
  location_code: string
  airport_code: string | null
  iata_code: string | null
  status: string | null
}

interface LocationAccessResult {
  // Whether the user has access to multiple locations
  hasMultipleLocations: boolean

  // All locations the user has access to (from TenantContext)
  accessibleLocations: Location[]

  // Currently selected location IDs for filtering ('all' or array of IDs)
  selectedLocationIds: string[] | 'all'

  // Update the selected location IDs
  setSelectedLocationIds: (ids: string[] | 'all') => void

  // Filtered locations based on selection (if 'all', returns all; otherwise filtered)
  filteredLocations: Location[]

  // Whether data should be filtered by location (for use in queries)
  shouldFilterByLocation: boolean

  // Location IDs to use for filtering (undefined if showing all)
  filterLocationIds: string[] | undefined
}

/**
 * Hook that provides location access information and filtering capabilities.
 *
 * Combines location data from TenantContext with user's location filter preferences
 * from UserPreferencesContext.
 *
 * Usage:
 * - `hasMultipleLocations` - Show/hide location selector UI
 * - `selectedLocationIds` - Current filter state
 * - `filterLocationIds` - Pass to API queries for location filtering
 *
 * For single-location users (hasMultipleLocations=false):
 * - Location selector should be hidden
 * - Data is automatically scoped to their single location
 *
 * For multi-location users:
 * - Show location selector in header
 * - Allow filtering by one or more locations
 * - 'all' shows data from all accessible locations
 */
export function useLocationAccess(): LocationAccessResult {
  const { locations } = useTenant()
  const { selectedLocationIds, setSelectedLocationIds } = useUserPreferences()

  const hasMultipleLocations = locations.length > 1

  // Compute filtered locations based on selection
  const filteredLocations = useMemo(() => {
    if (selectedLocationIds === 'all') {
      return locations
    }
    return locations.filter((loc) => selectedLocationIds.includes(loc.id))
  }, [locations, selectedLocationIds])

  // Determine if we should filter and what IDs to use
  const shouldFilterByLocation = selectedLocationIds !== 'all' && selectedLocationIds.length > 0
  const filterLocationIds = useMemo(() => {
    if (selectedLocationIds === 'all') {
      return undefined
    }
    // If no locations selected, return undefined (show all)
    if (selectedLocationIds.length === 0) {
      return undefined
    }
    return selectedLocationIds
  }, [selectedLocationIds])

  return {
    hasMultipleLocations,
    accessibleLocations: locations,
    selectedLocationIds,
    setSelectedLocationIds,
    filteredLocations,
    shouldFilterByLocation,
    filterLocationIds,
  }
}
